import { PropsWithChildren, useContext } from "react";
import { useTranslation } from "react-i18next";

import { Stack, SxProps, Theme } from "@mui/material";
import { FormikProps, FormikValues } from "formik";

import { FormikErrorNotification } from "../../forms/formik-error-notification/FormikErrorNotification";
import { MissingFieldMessages, MissingFieldsContext } from "../../forms/missing-fields/MissingFieldsContext";
import { SaveBar } from "../../save-bar/SaveBar";
import { ClientFileInfoLineContext } from "../context/ClientFileInfoLineContext";

interface Props<T extends FormikValues> extends PropsWithChildren {
  /**
   * Replaces the default reset/save row. A page passing this owns the info line's placement too:
   * read ClientFileInfoLineContext and group the line with the primary action (see
   * CaseNoteOverview) — or give it its own row when the actions row is crowded.
   */
  customActions?: React.ReactNode;
  formik?: FormikProps<T>;
  /** Empty mandatory answers of the saved data, shown on their fields without blocking the save. */
  missingFields?: MissingFieldMessages;
  submitDisabled?: boolean;
  sx?: SxProps<Theme>;
}

export function ClientContent<T extends FormikValues>({
  customActions,
  children,
  formik,
  missingFields,
  submitDisabled,
  sx,
}: Props<T>) {
  const { t } = useTranslation();
  const infoLine = useContext(ClientFileInfoLineContext)?.();

  return (
    <Stack component="form" onSubmit={formik?.handleSubmit} sx={{ flex: 1, maxHeight: "100%", gap: 1 }}>
      {formik && <FormikErrorNotification formik={formik} />}
      <Stack flex={1} gap={1} sx={[{ overflowY: "auto" }, ...(sx !== undefined ? [sx] : [])] as SxProps<Theme>}>
        {missingFields ? (
          <MissingFieldsContext.Provider value={missingFields}>{children}</MissingFieldsContext.Provider>
        ) : (
          children
        )}
      </Stack>
      {customActions ? (
        <Stack direction="row" justifyContent="space-between" alignItems="center" p={0.5}>
          {customActions}
        </Stack>
      ) : (
        formik && (
          <SaveBar
            resetLabel={t("common.resetChanges")}
            onReset={formik.handleReset}
            resetDisabled={formik.isSubmitting || !formik.dirty}
            saveLabel={t("common.save")}
            saveDisabled={formik.isSubmitting || !formik.dirty || submitDisabled}
            info={infoLine}
          />
        )
      )}
    </Stack>
  );
}
